import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";

interface DiditVerificationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purpose: "registration" | "withdrawal";
  onVerified?: () => void;
  onDeclined?: () => void;
}

const DiditVerification = ({ open, onOpenChange, purpose, onVerified, onDeclined }: DiditVerificationProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [verificationUrl, setVerificationUrl] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "verifying" | "approved" | "declined">("idle");

  const createSession = useCallback(async () => {
    if (!user || loading) return;
    setLoading(true);
    setStatus("loading");

    try {
      const { data, error } = await supabase.functions.invoke("didit-session", {
        body: { purpose },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setVerificationUrl(data.verification_url);
      setSessionId(data.session_id);
      setStatus("verifying");
    } catch (err: any) {
      console.error("Failed to create Didit session:", err);
      toast({
        title: "خطأ في إنشاء جلسة التحقق",
        description: err.message || "حاول مرة أخرى",
        variant: "destructive",
      });
      setStatus("idle");
    } finally {
      setLoading(false);
    }
  }, [user, purpose, toast, loading]);

  // Start session when dialog opens
  useEffect(() => {
    if (open && status === "idle") {
      createSession();
    }
    if (!open) {
      setStatus("idle");
      setVerificationUrl(null);
      setSessionId(null);
    }
  }, [open]);

  // Poll for verification status
  useEffect(() => {
    if (!sessionId || status !== "verifying") return;

    const interval = setInterval(async () => {
      try {
        const { data } = await supabase
          .from("id_verifications")
          .select("status, didit_status")
          .eq("user_id", user?.id)
          .eq("didit_session_id", sessionId)
          .maybeSingle();

        if (data?.status === "verified" || data?.didit_status === "Approved") {
          setStatus("approved");
          clearInterval(interval);
          toast({ title: "تم التحقق بنجاح ✅", description: "تم التحقق من هويتك بنجاح" });
          onVerified?.();
          setTimeout(() => onOpenChange(false), 2000);
        } else if (data?.status === "rejected" || data?.didit_status === "Declined") {
          setStatus("declined");
          clearInterval(interval);
          toast({ title: "فشل التحقق", description: "لم يتم التحقق من هويتك. حاول مرة أخرى.", variant: "destructive" });
          onDeclined?.();
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [sessionId, status, user?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden p-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <ShieldCheck className="w-5 h-5 text-primary" />
            التحقق من الهوية
          </DialogTitle>
          <DialogDescription>
            {purpose === "registration"
              ? "يرجى إكمال التحقق من هويتك لتفعيل حسابك"
              : "يرجى التحقق من هويتك لإتمام عملية السحب"}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-6">
          {status === "loading" && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
              <p className="text-muted-foreground">جارٍ إنشاء جلسة التحقق...</p>
            </div>
          )}

          {status === "verifying" && verificationUrl && (
            <div className="space-y-4">
              <iframe
                src={verificationUrl}
                className="w-full rounded-xl border border-border"
                style={{ height: "600px" }}
                allow="camera; microphone; fullscreen; autoplay; encrypted-media"
              />
              <p className="text-xs text-muted-foreground text-center">
                أكمل عملية التحقق في النافذة أعلاه. سيتم تحديث الحالة تلقائياً.
              </p>
            </div>
          )}

          {status === "approved" && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <CheckCircle2 className="w-16 h-16 text-green-500" />
              <p className="text-lg font-semibold text-foreground">تم التحقق بنجاح!</p>
              <p className="text-muted-foreground">تم التحقق من هويتك بنجاح</p>
            </div>
          )}

          {status === "declined" && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <AlertTriangle className="w-16 h-16 text-destructive" />
              <p className="text-lg font-semibold text-foreground">فشل التحقق</p>
              <p className="text-muted-foreground">لم يتم التحقق من هويتك. تأكد من وضوح الهوية والصورة.</p>
              <Button onClick={() => { setStatus("idle"); createSession(); }}>
                إعادة المحاولة
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DiditVerification;
